import { create } from 'zustand'
import { doc, getDoc, collection, getDocs } from 'firebase/firestore'
import { db } from '../config/firebase'

export interface VideoStat {
  videoId: string
  title: string
  totalPlays: number
  totalWatchedSeconds: number
  totalPauseCount: number
  totalFullscreenCount: number
  /** 切走分頁後背景續播的累計秒數（頁面內觀看 = totalWatchedSeconds - totalBackgroundSeconds），±10 秒 */
  totalBackgroundSeconds: number
}

export interface VideoWatcher {
  userId: string
  totalSeconds: number
  plays: number
  lastWatchedAt: Date | null
}

interface VideoStatsState {
  videoStats: VideoStat[]
  watchers: Record<string, VideoWatcher[]>
  loading: boolean
  error: string | null
  fetchVideoStats: () => Promise<void>
  fetchVideoWatchers: (videoId: string) => Promise<void>
}

export const useVideoStatsStore = create<VideoStatsState>((set, get) => ({
  videoStats: [],
  watchers: {},
  loading: false,
  error: null,

  fetchVideoStats: async () => {
    set({ loading: true, error: null })
    try {
      const snap = await getDoc(doc(db, 'analytics', 'videoStats'))
      if (!snap.exists()) {
        set({ videoStats: [], loading: false })
        return
      }
      const data = snap.data()
      const byVideo = data.byVideo as Record<string, Omit<VideoStat, 'videoId'>> | undefined
      if (!byVideo) {
        set({ videoStats: [], loading: false })
        return
      }
      const stats: VideoStat[] = Object.entries(byVideo).map(([videoId, v]) => ({
        videoId,
        title: v.title ?? videoId,
        totalPlays: v.totalPlays ?? 0,
        totalWatchedSeconds: v.totalWatchedSeconds ?? 0,
        totalPauseCount: v.totalPauseCount ?? 0,
        totalFullscreenCount: v.totalFullscreenCount ?? 0,
        totalBackgroundSeconds: v.totalBackgroundSeconds ?? 0,
      }))
      stats.sort((a, b) => b.totalPlays - a.totalPlays)
      set({ videoStats: stats, loading: false })
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },

  fetchVideoWatchers: async (videoId: string) => {
    const existing = get().watchers[videoId]
    if (existing) return
    try {
      const snap = await getDocs(
        collection(db, 'analytics', 'videoUsers', videoId)
      )
      const watchers: VideoWatcher[] = snap.docs.map((d) => ({
        userId: d.id,
        totalSeconds: d.data().totalSeconds ?? 0,
        plays: d.data().plays ?? 0,
        lastWatchedAt: d.data().lastWatchedAt?.toDate() ?? null,
      }))
      set((s) => ({ watchers: { ...s.watchers, [videoId]: watchers } }))
    } catch (err) {
      console.error('fetchVideoWatchers failed:', err)
    }
  },
}))
